/** Side-effecting controllers that mutate signals and talk to the API. */
import { api, ApiError, setCsrf } from "./api";
import * as S from "./state";
import type { ComposeMode } from "./state";
import type {
  BulkAction,
  Envelope,
  FullMessage,
  ListFilter,
  MailAddress,
  OutgoingAttachment,
  RemoteContentDefault,
  SaveDraftRequest,
  SearchOptions,
  SendRequest,
  SessionInfo,
  UserSettings,
} from "../shared/types";
import { prefs } from "./prefs";
import { t } from "./i18n";
import { sanitizeSignature } from "./util";

/* ------------------------------- session ------------------------------- */

function hydrateSession(info: SessionInfo): void {
  setCsrf(info.csrfToken);
  S.userEmail.value = info.email;
  S.isAdmin.value = info.isAdmin ?? false;
  S.defaultDomain.value = info.defaultDomain ?? "";
  S.passwordChangeEnabled.value = info.passwordChangeEnabled ?? false;
  S.passwordMinLength.value = info.passwordMinLength ?? 12;
  S.passwordMaxLength.value = info.passwordMaxLength ?? 64;
  S.remoteContentDefault.value = info.remoteContentDefault ?? "block";
  S.view.value = "app";
}

/** On first load, try to resume an existing session via the cookie. */
export async function bootstrap(): Promise<void> {
  try {
    const info = await api.session();
    hydrateSession(info);
    await loadMailboxes();
    await loadMessages();
  } catch {
    S.view.value = "login";
    return;
  }
  afterSignIn();
}

export async function login(email: string, password: string): Promise<void> {
  S.signingIn.value = true;
  S.authError.value = null;
  try {
    const info = await api.login({ email, password });
    hydrateSession(info);
    await loadMailboxes();
    await loadMessages();
    afterSignIn();
  } catch (e) {
    S.authError.value = e instanceof Error ? e.message : "Login failed";
  } finally {
    S.signingIn.value = false;
  }
}

/** Non-critical work after the mailbox is on screen. Never blocks or fails sign-in. */
function afterSignIn(): void {
  void loadUserSettings();
  startPolling();
}

export async function loadUserSettings(): Promise<void> {
  try {
    S.userSettings.value = await api.settings.get();
  } catch {
    /* keep defaults */
  }
}

export async function saveUserSettings(patch: Partial<UserSettings>): Promise<void> {
  try {
    S.userSettings.value = await api.settings.update(patch);
  } catch (e) {
    if (!handleAuthError(e)) throw e;
  }
}

export async function logout(): Promise<void> {
  stopPolling();
  try {
    await api.logout();
  } catch {
    // Ignore; we clear local state regardless.
  }
  setCsrf("");
  S.userEmail.value = "";
  S.isAdmin.value = false;
  S.defaultDomain.value = "";
  S.passwordChangeEnabled.value = false;
  S.passwordMinLength.value = 12;
  S.passwordMaxLength.value = 64;
  S.remoteContentDefault.value = "block";
  S.mailboxes.value = [];
  S.messages.value = [];
  S.messagesTotal.value = 0;
  S.selectedUid.value = null;
  S.selectedMailbox.value = null;
  S.selectedUids.value = new Set();
  S.currentMailbox.value = "INBOX";
  S.listFilter.value = "all";
  S.page.value = 1;
  S.fullMessage.value = null;
  S.searchResults.value = null;
  S.composer.value = null;
  S.composerError.value = null;
  S.composerAttachments.value = [];
  S.showSettings.value = false;
  S.view.value = "login";
}

/**
 * Persist the account's remote-content default through the API and reflect the
 * SERVER-CONFIRMED value in the signal (never the optimistic input, so the UI
 * stays truthful on failure). Re-throws non-auth failures so `RemoteContentPanel`
 * can surface a local error state (R4-001); a 401 is consumed by `handleAuthError`
 * (drops to login) and never surfaces to the panel.
 */
export async function setRemoteContentDefault(value: RemoteContentDefault): Promise<void> {
  try {
    const res = await api.account.updatePreferences({ remoteContentDefault: value });
    S.remoteContentDefault.value = res.remoteContentDefault;
  } catch (e) {
    if (!handleAuthError(e)) throw e;
  }
}

/** If the server says we are unauthenticated, drop to the login screen. */
function handleAuthError(e: unknown): boolean {
  if (e instanceof ApiError && e.status === 401) {
    void logout();
    return true;
  }
  return false;
}

function errorText(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

/* -------------------------------- toasts -------------------------------- */

let toastSeq = 0;

export function toast(
  message: string,
  opts: { tone?: S.Toast["tone"]; action?: S.Toast["action"]; duration?: number } = {},
): number {
  const id = ++toastSeq;
  const duration = opts.duration ?? (opts.action ? 7000 : 4000);
  // Keep the stack short: the newest three are plenty.
  S.toasts.value = [...S.toasts.value.slice(-2), { id, message, tone: opts.tone ?? "neutral", action: opts.action, duration }];
  if (duration > 0) setTimeout(() => dismissToast(id), duration);
  return id;
}

export function dismissToast(id: number): void {
  S.toasts.value = S.toasts.value.filter((x) => x.id !== id);
}

/** Promise-based confirmation dialog. */
export function confirmDialog(req: Omit<S.ConfirmRequest, "resolve">): Promise<boolean> {
  return new Promise((resolve) => {
    S.confirmRequest.value = {
      ...req,
      resolve: (ok) => {
        S.confirmRequest.value = null;
        resolve(ok);
      },
    };
  });
}

/* ------------------------------ mailboxes ------------------------------- */

export function specialPath(use: "archive" | "trash" | "junk" | "drafts" | "sent" | "inbox"): string | null {
  return S.mailboxes.value.find((m) => m.specialUse === use)?.path ?? null;
}

export async function loadMailboxes(): Promise<void> {
  try {
    S.mailboxes.value = await api.mailboxes.list();
  } catch (e) {
    handleAuthError(e);
  }
}

export async function selectMailbox(path: string): Promise<void> {
  S.mobilePane.value = "list";
  S.drawerOpen.value = false;
  if (S.isSearching.value) clearSearch();
  if (path === S.currentMailbox.value) return;
  S.currentMailbox.value = path;
  S.listFilter.value = "all";
  closeReader();
  S.selectedUids.value = new Set();
  S.selectionAnchor.value = null;
  await loadMessages();
}

export async function setListFilter(filter: ListFilter): Promise<void> {
  if (S.listFilter.value === filter) return;
  S.listFilter.value = filter;
  S.selectedUids.value = new Set();
  await loadMessages();
}

/** Fresh load of page 1 — used on mailbox switch, refresh, and initial load. */
export async function loadMessages(): Promise<void> {
  S.page.value = 1;
  S.listLoading.value = true;
  S.listError.value = null;
  const mailbox = S.currentMailbox.value;
  const filter = S.listFilter.value;
  try {
    const res = await api.messages(mailbox, 1, filter);
    // Ignore a response for a folder/filter the user already left.
    if (mailbox !== S.currentMailbox.value || filter !== S.listFilter.value) return;
    S.messages.value = res.envelopes;
    S.messagesTotal.value = res.total;
  } catch (e) {
    if (!handleAuthError(e)) {
      S.listError.value = e instanceof Error ? e.message : "Failed to load messages";
      S.messages.value = [];
    }
  } finally {
    S.listLoading.value = false;
  }
}

export async function refresh(): Promise<void> {
  S.refreshing.value = true;
  try {
    await Promise.all([loadMailboxes(), loadMessages()]);
  } finally {
    S.refreshing.value = false;
  }
}

/** Appends the next page of messages — driven by the list's scroll-to-bottom handler. */
export async function loadMoreMessages(): Promise<void> {
  if (S.loadingMore.value || S.listLoading.value || !S.hasMoreMessages.value) return;
  const nextPage = S.page.value + 1;
  S.loadingMore.value = true;
  try {
    const res = await api.messages(S.currentMailbox.value, nextPage, S.listFilter.value);
    const have = new Set(S.messages.value.map((m) => m.uid));
    S.messages.value = [...S.messages.value, ...res.envelopes.filter((m) => !have.has(m.uid))];
    S.messagesTotal.value = res.total;
    S.page.value = nextPage;
  } catch (e) {
    if (!handleAuthError(e)) {
      S.listError.value = e instanceof Error ? e.message : "Failed to load more messages";
    }
  } finally {
    S.loadingMore.value = false;
  }
}

function byDateDesc(a: Envelope, b: Envelope): number {
  return Date.parse(b.date) - Date.parse(a.date) || b.uid - a.uid;
}

/* ------------------------------ new mail poll ----------------------------- */

const POLL_MS = 45_000;
let pollTimer: ReturnType<typeof setInterval> | null = null;

function startPolling(): void {
  if (typeof document === "undefined" || pollTimer) return;
  pollTimer = setInterval(() => void checkForNewMail(), POLL_MS);
  document.addEventListener("visibilitychange", onVisible);
}

function stopPolling(): void {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
  if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisible);
}

function onVisible(): void {
  if (document.visibilityState === "visible") void checkForNewMail();
}

/**
 * Refresh folder counts and pull any new messages into the top of the open
 * folder without disturbing scroll position, selection, or the open message.
 */
export async function checkForNewMail(): Promise<void> {
  if (S.view.value !== "app") return;
  const before = S.mailboxes.value.find((m) => m.specialUse === "inbox")?.unread ?? 0;
  let boxes;
  try {
    boxes = await api.mailboxes.list();
  } catch (e) {
    handleAuthError(e);
    return;
  }
  S.mailboxes.value = boxes;
  const inboxUnread = boxes.find((m) => m.specialUse === "inbox")?.unread ?? 0;
  if (inboxUnread > before) notifyNewMail(inboxUnread - before);

  const box = boxes.find((m) => m.path === S.currentMailbox.value);
  if (!box || S.listLoading.value) return;
  try {
    const mailbox = S.currentMailbox.value;
    const res = await api.messages(mailbox, 1, S.listFilter.value);
    if (mailbox !== S.currentMailbox.value) return;
    const have = new Set(S.messages.value.map((m) => m.uid));
    const fresh = res.envelopes.filter((m) => !have.has(m.uid));
    // Refresh flags of what's already loaded on page 1 (read elsewhere, etc.).
    const byUid = new Map(res.envelopes.map((m) => [m.uid, m]));
    const merged = S.messages.value.map((m) => {
      const r = byUid.get(m.uid);
      return r ? { ...m, seen: r.seen, flagged: r.flagged, answered: r.answered } : m;
    });
    S.messages.value = fresh.length ? [...fresh, ...merged].sort(byDateDesc) : merged;
    S.messagesTotal.value = res.total;
  } catch {
    /* next tick will retry */
  }
}

function notifyNewMail(count: number): void {
  if (typeof document === "undefined" || !prefs.value.notifications) return;
  if (document.visibilityState === "visible") return;
  try {
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      const n = new Notification(count === 1 ? t("notify.one") : t("notify.many", { n: count }), {
        icon: "/favicon.svg",
        tag: "twm-new-mail",
      });
      n.onclick = () => {
        window.focus();
        void selectMailbox(specialPath("inbox") ?? "INBOX");
      };
    }
  } catch {
    /* notifications unavailable */
  }
}

export async function enableNotifications(): Promise<boolean> {
  if (typeof Notification === "undefined") return false;
  const res = await Notification.requestPermission();
  return res === "granted";
}

/* ------------------------------- reading ------------------------------- */

function openMailboxOf(): string {
  return S.selectedMailbox.value ?? S.currentMailbox.value;
}

export function closeReader(): void {
  S.selectedUid.value = null;
  S.selectedMailbox.value = null;
  S.fullMessage.value = null;
  S.thread.value = [];
  S.messageError.value = null;
}

export async function openMessage(uid: number, mailbox?: string): Promise<void> {
  const mbox = mailbox ?? S.currentMailbox.value;
  // Drafts open straight into the composer.
  const box = S.mailboxes.value.find((m) => m.path === mbox);
  if (box?.specialUse === "drafts") {
    await resumeDraft(uid, mbox);
    return;
  }
  S.mobilePane.value = "reader";
  S.selectedUid.value = uid;
  S.selectedMailbox.value = mbox === S.currentMailbox.value ? null : mbox;
  S.showRemote.value = false;
  S.fullMessage.value = null;
  S.thread.value = [];
  await loadFullMessage(false);
  void markSeenOnOpen(uid);
}

export async function loadFullMessage(remote: boolean): Promise<void> {
  const uid = S.selectedUid.value;
  if (uid == null) return;
  const mailbox = openMailboxOf();
  S.messageLoading.value = true;
  S.messageError.value = null;
  try {
    const full = await api.message(mailbox, uid, remote);
    if (S.selectedUid.value !== uid) return; // user moved on
    S.fullMessage.value = full;
    S.showRemote.value = remote;
    if (!remote) void loadThread(mailbox, uid);
  } catch (e) {
    if (!handleAuthError(e)) {
      S.messageError.value = e instanceof Error ? e.message : "Failed to load message";
    }
  } finally {
    S.messageLoading.value = false;
  }
}

async function loadThread(mailbox: string, uid: number): Promise<void> {
  try {
    const list = await api.thread(mailbox, uid);
    if (S.selectedUid.value === uid) S.thread.value = list.length > 1 ? list : [];
  } catch {
    S.thread.value = [];
  }
}

export function allowRemoteContent(): void {
  void loadFullMessage(true);
}

/** Open the next (+1) or previous (-1) message of the active list. */
export function openNeighbor(delta: 1 | -1): void {
  const list = S.activeList.value;
  if (list.length === 0) return;
  const mbox = openMailboxOf();
  const idx = list.findIndex((m) => m.uid === S.selectedUid.value && m.mailbox === mbox);
  const next = idx === -1 ? list[0] : list[Math.min(Math.max(idx + delta, 0), list.length - 1)];
  if (!next || (idx !== -1 && list[idx] === next)) return;
  void openMessage(next.uid, next.mailbox);
  if (typeof document !== "undefined") {
    requestAnimationFrame(() => {
      document.querySelector(`[data-uid="${String(next.uid)}"]`)?.scrollIntoView({ block: "nearest" });
    });
  }
}

/* --------------------------- .eml attachment preview --------------------------- */

/**
 * Open the nested-message preview for a `.eml`/`message/rfc822` attachment on
 * the currently-open message. Drives the `emlPreview*` signals that back the
 * modal. Any failure resolves to a clear error state — it never throws or
 * disturbs the reader.
 */
export async function openEmlPreview(partId: string): Promise<void> {
  const uid = S.selectedUid.value;
  if (uid == null) return;
  const mailbox = openMailboxOf();
  S.emlPreviewLoading.value = true;
  S.emlPreviewError.value = null;
  S.emlPreview.value = null;
  try {
    S.emlPreview.value = await api.attachmentPreview(mailbox, uid, partId);
  } catch (e) {
    if (!handleAuthError(e)) {
      // English copy is pinned by tests: "Could not preview this attachment."
      S.emlPreviewError.value = t("eml.error");
    }
  } finally {
    S.emlPreviewLoading.value = false;
  }
}

/** Close the preview modal and reset its signals. */
export function closeEmlPreview(): void {
  S.emlPreview.value = null;
  S.emlPreviewError.value = null;
  S.emlPreviewLoading.value = false;
}

const INLINE_VIEWABLE = /^(image\/(png|jpe?g|gif|webp|avif)|application\/pdf|text\/plain)$/i;

export function canViewInline(contentType: string): boolean {
  return INLINE_VIEWABLE.test(contentType);
}

export function openAttachmentViewer(partId: string, filename: string, contentType: string): void {
  const m = S.fullMessage.value;
  if (!m) return;
  S.attachmentViewer.value = {
    url: api.attachmentUrl(m.mailbox, m.uid, partId, true),
    downloadUrl: api.attachmentUrl(m.mailbox, m.uid, partId),
    filename,
    contentType,
  };
}

/* ---------------------------- message actions ---------------------------- */

interface Ref {
  mailbox: string;
  uid: number;
}

function sameRef(a: Ref, b: Ref): boolean {
  return a.uid === b.uid && a.mailbox === b.mailbox;
}

/** Apply a patch to every copy of the given messages (list, search, thread, reader). */
function patchEnvelopes(refs: Ref[], patch: Partial<Envelope>): void {
  const has = (m: Envelope): boolean => refs.some((r) => sameRef(r, m));
  if (refs.some((r) => r.mailbox === S.currentMailbox.value)) {
    S.messages.value = S.messages.value.map((m) => (has(m) ? { ...m, ...patch } : m));
  }
  if (S.searchResults.value) S.searchResults.value = S.searchResults.value.map((m) => (has(m) ? { ...m, ...patch } : m));
  if (S.thread.value.length) S.thread.value = S.thread.value.map((m) => (has(m) ? { ...m, ...patch } : m));
  const fm = S.fullMessage.value;
  if (fm && has(fm)) S.fullMessage.value = { ...fm, ...patch };
}

function findEnvelope(ref: Ref): Envelope | undefined {
  return (
    S.activeList.value.find((m) => sameRef(m, ref)) ??
    S.messages.value.find((m) => sameRef(m, ref)) ??
    (S.fullMessage.value && sameRef(S.fullMessage.value, ref) ? S.fullMessage.value : undefined)
  );
}

function adjustUnread(mailbox: string, delta: number): void {
  if (!delta) return;
  S.mailboxes.value = S.mailboxes.value.map((mb) =>
    mb.path === mailbox ? { ...mb, unread: Math.max(0, mb.unread + delta) } : mb,
  );
}

function groupByMailbox(refs: Ref[]): Map<string, number[]> {
  const map = new Map<string, number[]>();
  for (const r of refs) map.set(r.mailbox, [...(map.get(r.mailbox) ?? []), r.uid]);
  return map;
}

/** Set read/unread or star flags, optimistically, for any number of messages. */
export async function setFlags(refs: Ref[], patch: { seen?: boolean; flagged?: boolean }): Promise<void> {
  if (refs.length === 0) return;
  const before = refs.map((r) => ({ r, env: findEnvelope(r) }));
  patchEnvelopes(refs, patch);
  if (patch.seen !== undefined) {
    for (const { r, env } of before) if (env && env.seen !== patch.seen) adjustUnread(r.mailbox, patch.seen ? -1 : 1);
  }
  try {
    for (const [mailbox, uids] of groupByMailbox(refs)) {
      const action: BulkAction =
        patch.seen !== undefined ? (patch.seen ? "read" : "unread") : patch.flagged ? "flag" : "unflag";
      await api.bulk({ mailbox, uids, action });
    }
  } catch (e) {
    if (handleAuthError(e)) return;
    // Roll back to what each message had before.
    for (const { r, env } of before) {
      if (env) patchEnvelopes([r], { seen: env.seen, flagged: env.flagged });
    }
    void loadMailboxes();
    toast(t("toast.actionFailed"), { tone: "error" });
  }
}

export async function toggleSeen(uid: number, mailbox = S.currentMailbox.value): Promise<void> {
  const env = findEnvelope({ uid, mailbox });
  if (!env) return;
  await setFlags([{ uid, mailbox }], { seen: !env.seen });
}

export async function toggleFlagged(uid: number, mailbox = S.currentMailbox.value): Promise<void> {
  const env = findEnvelope({ uid, mailbox });
  if (!env) return;
  await setFlags([{ uid, mailbox }], { flagged: !env.flagged });
}

/** Mark seen automatically when a message is opened (called by openMessage). */
export async function markSeenOnOpen(uid: number): Promise<void> {
  const mailbox = openMailboxOf();
  const env = findEnvelope({ uid, mailbox });
  if (!env || env.seen) return;
  patchEnvelopes([{ uid, mailbox }], { seen: true });
  adjustUnread(mailbox, -1);
  try {
    await api.bulk({ mailbox, uids: [uid], action: "read" });
  } catch {
    // Non-fatal: the next refresh will show the real state.
  }
}

/**
 * Remove messages from view and move them (or delete them). Optimistic: the
 * list updates instantly, and failures restore it. Moves offer an Undo that
 * moves the messages back using the server-reported UID mapping.
 */
async function relocate(refs: Ref[], action: "move" | "delete", destination?: string): Promise<void> {
  if (refs.length === 0) return;
  const prev = {
    messages: S.messages.value,
    total: S.messagesTotal.value,
    search: S.searchResults.value,
  };
  const removing = (m: Envelope): boolean => refs.some((r) => sameRef(r, m));

  // Decide what to show next if the open message is going away.
  const openRef = S.selectedUid.value != null ? { uid: S.selectedUid.value, mailbox: openMailboxOf() } : null;
  let nextOpen: Envelope | null = null;
  if (openRef && refs.some((r) => sameRef(r, openRef))) {
    const list = S.activeList.value;
    const idx = list.findIndex((m) => sameRef(m, openRef));
    const remaining = list.filter((m) => !removing(m));
    if (prefs.value.afterMove === "next" && remaining.length) {
      nextOpen = remaining[Math.min(Math.max(idx, 0), remaining.length - 1)] ?? null;
    }
  }

  const removedHere = S.messages.value.filter(removing).length;
  S.messages.value = S.messages.value.filter((m) => !removing(m));
  S.messagesTotal.value = Math.max(0, S.messagesTotal.value - removedHere);
  if (S.searchResults.value) S.searchResults.value = S.searchResults.value.filter((m) => !removing(m));
  S.selectedUids.value = new Set([...S.selectedUids.value].filter((uid) => !refs.some((r) => r.uid === uid)));

  if (openRef && refs.some((r) => sameRef(r, openRef))) {
    closeReader();
    if (nextOpen && typeof document !== "undefined" && window.matchMedia("(min-width: 768px)").matches) {
      void openMessage(nextOpen.uid, nextOpen.mailbox);
    } else {
      S.mobilePane.value = "list";
    }
  }

  const undoBatches: Array<{ from: string; uids: number[]; to: string }> = [];
  try {
    for (const [mailbox, uids] of groupByMailbox(refs)) {
      const res = await api.bulk({ mailbox, uids, action, destination });
      const moved = Object.values(res?.uidMap ?? {});
      if (res?.destination && moved.length) undoBatches.push({ from: res.destination, uids: moved, to: mailbox });
    }
  } catch (e) {
    if (handleAuthError(e)) return;
    S.messages.value = prev.messages;
    S.messagesTotal.value = prev.total;
    S.searchResults.value = prev.search;
    toast(errorText(e, t("toast.actionFailed")), { tone: "error" });
    return;
  } finally {
    void loadMailboxes();
  }

  const n = refs.length;
  const destBox = S.mailboxes.value.find((m) => m.path === (destination ?? undoBatches[0]?.from));
  const label =
    action === "delete"
      ? undoBatches.length
        ? t("toast.trashed", { n })
        : t("toast.deleted", { n })
      : destBox?.specialUse === "archive"
        ? t("toast.archived", { n })
        : destBox?.specialUse === "junk"
          ? t("toast.spammed", { n })
          : t("toast.moved", { n, folder: folderLabel(destBox?.path ?? destination ?? "") });

  toast(label, {
    action: undoBatches.length
      ? {
          label: t("common.undo"),
          run: () => void undoRelocate(undoBatches),
        }
      : undefined,
  });
}

async function undoRelocate(batches: Array<{ from: string; uids: number[]; to: string }>): Promise<void> {
  try {
    for (const b of batches) await api.bulk({ mailbox: b.from, uids: b.uids, action: "move", destination: b.to });
    toast(t("toast.undone"));
  } catch (e) {
    if (!handleAuthError(e)) toast(errorText(e, t("toast.actionFailed")), { tone: "error" });
  }
  await refresh();
}

export async function moveMessages(refs: Ref[], destination: string): Promise<void> {
  const movable = refs.filter((r) => r.mailbox !== destination);
  await relocate(movable, "move", destination);
}

export async function archiveMessages(refs: Ref[]): Promise<void> {
  const archive = specialPath("archive");
  if (!archive) {
    toast(t("toast.noArchive"), { tone: "error" });
    return;
  }
  await moveMessages(refs, archive);
}

export async function spamMessages(refs: Ref[]): Promise<void> {
  const junk = specialPath("junk");
  if (!junk) {
    toast(t("toast.noSpam"), { tone: "error" });
    return;
  }
  await moveMessages(refs, junk);
}

export async function notSpamMessages(refs: Ref[]): Promise<void> {
  await moveMessages(refs, specialPath("inbox") ?? "INBOX");
}

/** Delete: moves to Trash, or erases for good (after confirming) when already there. */
export async function deleteMessages(refs: Ref[]): Promise<void> {
  if (refs.length === 0) return;
  const trash = specialPath("trash");
  const permanent = !trash || refs.every((r) => r.mailbox === trash);
  if (permanent) {
    const ok = await confirmDialog({
      title: t("confirm.deleteForever.title", { n: refs.length }),
      body: t("confirm.deleteForever.body"),
      confirmLabel: t("common.deleteForever"),
      danger: true,
    });
    if (!ok) return;
  }
  await relocate(refs, "delete");
}

/* Legacy single-message helpers (current folder), kept for existing callers. */
export async function moveMessage(uid: number, destination: string): Promise<void> {
  await moveMessages([{ uid, mailbox: S.currentMailbox.value }], destination);
}

export async function deleteMessage(uid: number): Promise<void> {
  await deleteMessages([{ uid, mailbox: S.currentMailbox.value }]);
}

/* ------------------------------- selection ------------------------------- */

/** Messages targeted by a bulk action: the checked ones, as refs. */
export function selectedRefs(): Ref[] {
  const sel = S.selectedUids.value;
  return S.activeList.value.filter((m) => sel.has(m.uid)).map((m) => ({ uid: m.uid, mailbox: m.mailbox }));
}

/** Bulk: move all selected UIDs to destination. */
export async function bulkMove(destination: string): Promise<void> {
  const refs = selectedRefs();
  S.selectedUids.value = new Set();
  await moveMessages(refs, destination);
}

/** Bulk: delete all selected UIDs. */
export async function bulkDelete(): Promise<void> {
  const refs = selectedRefs();
  await deleteMessages(refs);
}

/** Bulk: mark all selected as seen or unseen. */
export async function bulkMarkSeen(seen: boolean): Promise<void> {
  await setFlags(selectedRefs(), { seen });
}

export async function bulkFlag(flagged: boolean): Promise<void> {
  await setFlags(selectedRefs(), { flagged });
}

/** Toggle a UID in the bulk-select set; with `range`, select from the anchor to it. */
export function toggleSelect(uid: number, range = false): void {
  const cur = new Set(S.selectedUids.value);
  const anchor = S.selectionAnchor.value;
  if (range && anchor != null && anchor !== uid) {
    const list = S.activeList.value;
    const a = list.findIndex((m) => m.uid === anchor);
    const b = list.findIndex((m) => m.uid === uid);
    if (a !== -1 && b !== -1) {
      const [lo, hi] = a < b ? [a, b] : [b, a];
      for (const m of list.slice(lo, hi + 1)) cur.add(m.uid);
      S.selectedUids.value = cur;
      return;
    }
  }
  if (cur.has(uid)) cur.delete(uid);
  else cur.add(uid);
  S.selectedUids.value = cur;
  S.selectionAnchor.value = uid;
}

/** Select all visible messages. */
export function selectAll(): void {
  S.selectedUids.value = new Set(S.activeList.value.map((m) => m.uid));
}

/** Clear selection. */
export function clearSelection(): void {
  S.selectedUids.value = new Set();
  S.selectionAnchor.value = null;
}

/* -------------------------------- search -------------------------------- */

function hasAdvanced(o: SearchOptions): boolean {
  return Boolean(o.unread || o.flagged || o.since || o.before || o.allFolders || (o.field && o.field !== "all"));
}

/** Run a search in the current mailbox (or everywhere). Sets searchResults signal. */
export async function runSearch(query: string, opts: SearchOptions = S.searchOptions.value): Promise<void> {
  const q = query.trim();
  S.searchQuery.value = q;
  S.searchOptions.value = opts;
  if (!q && !hasAdvanced(opts)) {
    S.searchResults.value = null;
    return;
  }
  S.searchLoading.value = true;
  S.searchError.value = null;
  S.selectedUids.value = new Set();
  try {
    const res = hasAdvanced(opts)
      ? await api.advancedSearch(S.currentMailbox.value, q, opts)
      : await api.search(S.currentMailbox.value, q);
    // Drop stale responses from an earlier keystroke.
    if (S.searchQuery.value !== q) return;
    S.searchResults.value = res.envelopes;
  } catch (e) {
    if (!handleAuthError(e)) {
      S.searchError.value = e instanceof Error ? e.message : "Search failed";
      S.searchResults.value = [];
    }
  } finally {
    S.searchLoading.value = false;
  }
}

/** Clear search and return to normal list view. */
export function clearSearch(): void {
  S.searchQuery.value = "";
  S.searchOptions.value = {};
  S.searchResults.value = null;
  S.searchError.value = null;
}

/* -------------------------------- composer ------------------------------- */

function fmtAddr(a: MailAddress): string {
  return a.name ? `${a.name} <${a.address}>` : a.address;
}

function joinAddrs(list: MailAddress[]): string {
  return list.map(fmtAddr).join(", ");
}

/** Parse a comma/semicolon-separated input into bare email addresses. */
export function parseRecipients(input: string): string[] {
  return input
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => {
      // Accept "Name <addr>" and bare "addr"; keep only the address.
      const m = s.match(/<([^>]+)>/);
      return (m ? m[1]! : s).trim();
    })
    .filter(Boolean);
}

function ensureSubjectPrefix(subject: string, prefix: "Re" | "Fwd"): string {
  const s = subject.trim();
  const re = new RegExp(`^${prefix}:`, "i");
  if (re.test(s)) return s;
  // Treat the opposite prefix as already-threaded too (don't double-stack).
  return `${prefix}: ${s || "(no subject)"}`;
}

function quoteHeader(m: FullMessage): string {
  const on = new Date(m.date).toLocaleString();
  const who = m.from[0] ? fmtAddr(m.from[0]) : "someone";
  return t("compose.quoteHeader", { date: on, who: escapeHtml(who) });
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function textToHtml(text: string): string {
  return escapeHtml(text).replace(/\n/g, "<br>");
}

/** Build an HTML quoted body from the original message. */
function quotedBody(m: FullMessage): string {
  const bodyContent = m.html ? m.html : textToHtml(m.text ?? "");
  return `<blockquote style="margin:0 0 0 4px;border-left:2px solid #ccc;padding-left:10px;color:#555">${quoteHeader(m)}<br>${bodyContent}</blockquote>`;
}

function forwardedBody(m: FullMessage): string {
  const bodyContent = m.html ? m.html : textToHtml(m.text ?? "");
  return [
    '<div style="border-left:2px solid #ccc;padding-left:10px;color:#555">',
    `<b>${t("compose.fwdHeader")}</b><br>`,
    `<b>${t("compose.from")}:</b> ${m.from[0] ? escapeHtml(fmtAddr(m.from[0])) : ""}<br>`,
    `<b>${t("compose.date")}:</b> ${new Date(m.date).toLocaleString()}<br>`,
    `<b>${t("compose.subject")}:</b> ${escapeHtml(m.subject || "(no subject)")}<br>`,
    `<b>${t("compose.to")}:</b> ${escapeHtml(joinAddrs(m.to))}<br>`,
    m.cc.length ? `<b>Cc:</b> ${escapeHtml(joinAddrs(m.cc))}<br>` : "",
    "<br>",
    bodyContent,
    "</div>",
  ].join("");
}

/** The user's signature block, or "" when none is set. */
function signatureBlock(): string {
  const sig = S.userSettings.value.signature.trim();
  if (!sig) return "";
  return `<div data-signature="1">-- <br>${sanitizeSignature(sig)}</div>`;
}

/** Build the reply References chain: existing chain + the message's own id. */
function buildReferences(m: FullMessage): string[] {
  const refs = [...m.references];
  if (m.messageId && !refs.includes(m.messageId)) refs.push(m.messageId);
  return refs;
}

function startComposer(draft: S.ComposerDraft, draftUid: number | null = null): void {
  // A send waiting in the undo window goes out now rather than being lost.
  if (S.composerPendingSend.value) flushPendingSend();
  S.composerError.value = null;
  S.composerDraftUid.value = draftUid;
  S.composerLastSaved.value = null;
  S.composerAttachments.value = [];
  S.composerWindow.value = "normal";
  S.composer.value = draft;
  S.composerInitial.value = { ...draft };
  S.composerSession.value += 1;
}

export function openCompose(prefill: { to?: string; subject?: string } = {}): void {
  const sig = signatureBlock();
  startComposer({
    mode: "new",
    to: prefill.to ?? "",
    cc: "",
    bcc: "",
    subject: prefill.subject ?? "",
    body: sig ? `<br><br>${sig}` : "",
    showCc: false,
  });
}

/** Open the composer prefilled as a reply / reply-all / forward. */
export function openComposeFor(mode: ComposeMode): void {
  const m = S.fullMessage.value;
  if (!m || mode === "new" || mode === "draft") {
    openCompose();
    return;
  }
  const me = S.userEmail.value.toLowerCase();
  const sig = signatureBlock();
  const sigAbove = sig && S.userSettings.value.signatureOnReplies ? `<br><br>${sig}` : "";

  if (mode === "forward") {
    startComposer({
      mode,
      to: "",
      cc: "",
      bcc: "",
      subject: ensureSubjectPrefix(m.subject, "Fwd"),
      body: `${sigAbove}<br><br>${forwardedBody(m)}`,
      inReplyTo: m.messageId ?? undefined,
      references: buildReferences(m),
      showCc: false,
    });
    void attachOriginalFiles(m);
    return;
  }

  // reply / replyAll — prefer Reply-To, else From. Replying to your own sent
  // message goes back to its original recipients.
  const fromMe = m.from[0]?.address.toLowerCase() === me;
  const replyTarget = fromMe ? m.to : m.replyTo.length ? m.replyTo : m.from;
  const to = joinAddrs(replyTarget);

  let cc = "";
  if (mode === "replyAll") {
    // Everyone on To + Cc, minus ourselves and the people already in To.
    const targetAddrs = new Set(replyTarget.map((a) => a.address.toLowerCase()));
    const extras = [...m.to, ...m.cc].filter((a) => {
      const lc = a.address.toLowerCase();
      return lc !== me && !targetAddrs.has(lc);
    });
    cc = joinAddrs(dedupeAddrs(extras));
  }

  startComposer({
    mode,
    to,
    cc,
    bcc: "",
    subject: ensureSubjectPrefix(m.subject, "Re"),
    body: `${sigAbove}<br><br>${quotedBody(m)}`,
    inReplyTo: m.messageId ?? undefined,
    references: buildReferences(m),
    showCc: cc.length > 0,
  });
}

/** Forwarding keeps the original's (non-inline) attachments. */
async function attachOriginalFiles(m: FullMessage): Promise<void> {
  const files = m.attachments.filter((a) => !a.inline);
  for (const a of files) {
    try {
      const res = await fetch(api.attachmentUrl(m.mailbox, m.uid, a.partId), { credentials: "same-origin" });
      if (!res.ok) continue;
      const blob = await res.blob();
      if (!S.composer.value) return;
      await addAttachments([new File([blob], a.filename, { type: a.contentType })], true);
    } catch {
      /* skip unreadable attachment */
    }
  }
}

function dedupeAddrs(list: MailAddress[]): MailAddress[] {
  const seen = new Set<string>();
  const out: MailAddress[] = [];
  for (const a of list) {
    const k = a.address.toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(a);
  }
  return out;
}

/** Re-open a saved draft in the composer, including its attachments. */
export async function resumeDraft(uid: number, mailbox: string): Promise<void> {
  let m: FullMessage;
  try {
    m = await api.message(mailbox, uid, false);
  } catch (e) {
    if (!handleAuthError(e)) toast(errorText(e, t("toast.actionFailed")), { tone: "error" });
    return;
  }
  const draft: S.ComposerDraft = {
    mode: "draft",
    to: joinAddrs(m.to),
    cc: joinAddrs(m.cc),
    bcc: joinAddrs(m.bcc),
    subject: m.subject,
    body: m.html ?? textToHtml(m.text ?? ""),
    inReplyTo: undefined,
    references: m.references.length ? m.references : undefined,
    showCc: m.cc.length > 0 || m.bcc.length > 0,
  };
  startComposer(draft, uid);
  await attachOriginalFiles(m);
  // Attachments loaded from the server are not unsaved changes.
  S.composerInitial.value = S.composer.value ? { ...S.composer.value } : null;
}

export function closeComposer(): void {
  S.composer.value = null;
  S.composerInitial.value = null;
  S.composerError.value = null;
  S.composerSending.value = false;
  S.composerShowConfirm.value = false;
  S.composerPendingSend.value = false;
  S.composerAttachments.value = [];
  S.composerWindow.value = "normal";
  clearDraftTimer();
}

/**
 * Safe close: shows a confirmation dialog if the composer has unsaved changes.
 * Does nothing while a save is in flight.
 */
export function closeComposerSafe(): void {
  const draft = S.composer.value;
  if (!draft) {
    closeComposer();
    return;
  }
  // Block close while a save is in flight.
  if (S.composerSaving.value) return;

  if (S.composerDirty.value) {
    S.composerShowConfirm.value = true;
    return;
  }
  closeComposer();
}

/** Action called by the confirmation dialog: discard changes and close. */
export function discardComposer(): void {
  S.composerShowConfirm.value = false;
  closeComposer();
}

/** Discard and also remove the autosaved draft from the Drafts folder. */
export async function discardDraft(): Promise<void> {
  const uid = S.composerDraftUid.value;
  closeComposer();
  if (uid != null) {
    try {
      await api.deleteMessage(specialPath("drafts") ?? "Drafts", uid);
      if (S.currentMailboxObj.value?.specialUse === "drafts") void loadMessages();
    } catch {
      /* the draft stays; harmless */
    }
  }
  toast(t("toast.draftDiscarded"));
}

/** Action called by the confirmation dialog: save draft then close. */
export async function saveAndCloseComposer(): Promise<void> {
  S.composerShowConfirm.value = false;
  await saveDraft();
  closeComposer();
}

/* ---------------------------- draft autosave ----------------------------- */

let draftSaveTimer: ReturnType<typeof setTimeout> | null = null;

function clearDraftTimer(): void {
  if (draftSaveTimer !== null) {
    clearTimeout(draftSaveTimer);
    draftSaveTimer = null;
  }
}

function scheduleDraftSave(): void {
  clearDraftTimer();
  draftSaveTimer = setTimeout(() => {
    draftSaveTimer = null;
    void saveDraft();
  }, 3000);
}

export function updateComposer(patch: Partial<S.ComposerDraft>): void {
  const cur = S.composer.value;
  if (!cur) return;
  S.composer.value = { ...cur, ...patch };
  scheduleDraftSave();
}

/* ------------------------------ attachments ------------------------------ */

export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;
let attachSeq = 0;

function readAsBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result ?? "");
      resolve(s.slice(s.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Add files to the composer. Enforces the 25 MB total the server accepts. */
export async function addAttachments(files: File[], silent = false): Promise<void> {
  if (!S.composer.value || files.length === 0) return;
  let total = S.composerAttachments.value.reduce((n, a) => n + a.size, 0);
  const accepted: Array<{ file: File; item: S.ComposerAttachment }> = [];
  for (const file of files) {
    if (total + file.size > MAX_ATTACHMENT_BYTES) {
      if (!silent) toast(t("compose.tooLarge", { name: file.name }), { tone: "error" });
      continue;
    }
    total += file.size;
    accepted.push({
      file,
      item: {
        id: `a${++attachSeq}`,
        filename: file.name || "attachment",
        contentType: file.type || "application/octet-stream",
        size: file.size,
        content: null,
      },
    });
  }
  if (!accepted.length) return;
  S.composerAttachments.value = [...S.composerAttachments.value, ...accepted.map((a) => a.item)];
  syncAttachmentIds();
  await Promise.all(
    accepted.map(async ({ file, item }) => {
      try {
        const content = await readAsBase64(file);
        S.composerAttachments.value = S.composerAttachments.value.map((a) => (a.id === item.id ? { ...a, content } : a));
      } catch {
        removeAttachment(item.id);
        toast(t("compose.readFailed", { name: item.filename }), { tone: "error" });
      }
    }),
  );
  scheduleDraftSave();
}

export function removeAttachment(id: string): void {
  S.composerAttachments.value = S.composerAttachments.value.filter((a) => a.id !== id);
  syncAttachmentIds();
  scheduleDraftSave();
}

function syncAttachmentIds(): void {
  const cur = S.composer.value;
  if (!cur) return;
  S.composer.value = { ...cur, attachmentIds: S.composerAttachments.value.map((a) => a.id) };
}

function outgoingAttachments(): OutgoingAttachment[] | undefined {
  const list = S.composerAttachments.value.filter((a) => a.content !== null);
  if (!list.length) return undefined;
  return list.map((a) => ({ filename: a.filename, contentType: a.contentType, content: a.content! }));
}

function htmlToText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|blockquote)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, "&")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Persist the current composer state as an IMAP draft.
 * Gates: null composer, already saving, empty draft, unchanged since last save.
 * Uses POST on first save, PUT on subsequent saves (replace pattern).
 */
export async function saveDraft(): Promise<void> {
  const draft = S.composer.value;
  if (!draft || S.composerSaving.value) return;

  const toList = parseRecipients(draft.to);
  const ccList = parseRecipients(draft.cc);
  const bccList = parseRecipients(draft.bcc);
  const hasRecipient = toList.length > 0 || ccList.length > 0 || bccList.length > 0;
  const subject = draft.subject.trim();
  const body = draft.body.trim();

  // Don't autosave empty drafts.
  if (!hasRecipient && !subject && !body && S.composerAttachments.value.length === 0) return;

  // Skip if nothing changed since the last successful save.
  const last = S.composerLastSaved.value;
  if (last && JSON.stringify(draft) === JSON.stringify(last)) return;

  S.composerSaving.value = true;

  const bodyIsHtml = /<[a-z]/i.test(draft.body);
  const attachments = outgoingAttachments();
  const req: SaveDraftRequest = {
    to: toList,
    cc: ccList.length ? ccList : undefined,
    bcc: bccList.length ? bccList : undefined,
    subject: draft.subject,
    html: bodyIsHtml ? draft.body : undefined,
    text: bodyIsHtml ? htmlToText(draft.body) : draft.body,
    inReplyTo: draft.inReplyTo,
    references: draft.references?.length ? draft.references : undefined,
    ...(attachments ? { attachments } : {}),
  };

  const existingUid = S.composerDraftUid.value;
  try {
    const res =
      existingUid != null
        ? await api.drafts.update(existingUid, req)
        : await api.drafts.save(req);

    if (res.savedToDrafts) {
      S.composerDraftUid.value = res.uid;
      S.composerLastSaved.value = { ...draft };
    }
  } catch (e) {
    if (!handleAuthError(e)) {
      console.error("[actions] saveDraft failed:", e);
    }
  } finally {
    S.composerSaving.value = false;
  }
}

/* --------------------------------- sending -------------------------------- */

let pendingSendTimer: ReturnType<typeof setTimeout> | null = null;
let pendingToastId: number | null = null;

function validateRecipients(): boolean {
  const draft = S.composer.value;
  if (!draft) return false;
  const all = [...parseRecipients(draft.to), ...parseRecipients(draft.cc), ...parseRecipients(draft.bcc)];
  if (all.length === 0) {
    S.composerError.value = t("compose.needRecipient");
    return false;
  }
  const bad = all.find((a) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a));
  if (bad) {
    S.composerError.value = t("compose.badRecipient", { addr: bad });
    return false;
  }
  if (S.composerAttachments.value.some((a) => a.content === null)) {
    S.composerError.value = t("compose.stillAttaching");
    return false;
  }
  return true;
}

/**
 * Send with an undo window: the composer hides, a toast offers Undo, and the
 * message only leaves after `undoSendSeconds`. With 0 it sends immediately.
 */
export async function queueSend(): Promise<void> {
  const draft = S.composer.value;
  if (!draft || S.composerSending.value || S.composerPendingSend.value) return;
  S.composerError.value = null;
  if (!validateRecipients()) return;
  // Subject-less mail is usually a slip; ask before it goes out.
  if (!draft.subject.trim()) {
    const ok = await confirmDialog({
      title: t("confirm.noSubject.title"),
      body: t("confirm.noSubject.body"),
      confirmLabel: t("compose.sendAnyway"),
    });
    if (!ok || S.composer.value !== draft) return;
  }
  const seconds = prefs.value.undoSendSeconds;
  if (seconds <= 0) {
    void sendComposer();
    return;
  }
  clearDraftTimer();
  S.composerPendingSend.value = true;
  pendingToastId = toast(t("toast.sending"), {
    duration: seconds * 1000,
    action: { label: t("common.undo"), run: cancelPendingSend },
  });
  pendingSendTimer = setTimeout(() => flushPendingSend(), seconds * 1000);
}

function cancelPendingSend(): void {
  if (pendingSendTimer) clearTimeout(pendingSendTimer);
  pendingSendTimer = null;
  S.composerPendingSend.value = false;
  S.composerWindow.value = "normal";
}

function flushPendingSend(): void {
  if (pendingSendTimer) clearTimeout(pendingSendTimer);
  pendingSendTimer = null;
  if (pendingToastId != null) dismissToast(pendingToastId);
  pendingToastId = null;
  void sendComposer();
}

export async function sendComposer(): Promise<void> {
  const draft = S.composer.value;
  if (!draft || S.composerSending.value) return;

  const to = parseRecipients(draft.to);
  const cc = parseRecipients(draft.cc);
  const bcc = parseRecipients(draft.bcc);

  if (to.length === 0 && cc.length === 0 && bcc.length === 0) {
    S.composerError.value = "Add at least one recipient";
    S.composerPendingSend.value = false;
    return;
  }

  const bodyIsHtml = /<[a-z]/i.test(draft.body);
  const attachments = outgoingAttachments();
  const req: SendRequest = {
    to,
    cc: cc.length ? cc : undefined,
    bcc: bcc.length ? bcc : undefined,
    subject: draft.subject,
    html: bodyIsHtml ? draft.body : undefined,
    text: bodyIsHtml ? htmlToText(draft.body) : draft.body,
    inReplyTo: draft.inReplyTo,
    references: draft.references?.length ? draft.references : undefined,
    ...(attachments ? { attachments } : {}),
  };

  S.composerSending.value = true;
  S.composerError.value = null;
  try {
    const res = await api.send(req);
    // Clean up the draft if one was saved.
    const draftUid = S.composerDraftUid.value;
    if (draftUid != null) {
      const draftsMbox = S.mailboxes.value.find((mb) => mb.specialUse === "drafts");
      const draftsPath = draftsMbox?.path ?? "Drafts";
      api.deleteMessage(draftsPath, draftUid).catch(() => {
        // Non-critical: silently ignore draft cleanup failure.
      });
      S.composerDraftUid.value = null;
    }
    // Mark the original as answered locally.
    if (draft.mode === "reply" || draft.mode === "replyAll") {
      const fm = S.fullMessage.value;
      if (fm) patchEnvelopes([{ uid: fm.uid, mailbox: fm.mailbox }], { answered: true });
    }
    closeComposer();
    const sent = res.savedToSent ? t("toast.sent") : t("toast.sentNoCopy");
    S.composerNotice.value = sent;
    toast(sent, { tone: "success" });
    setTimeout(() => (S.composerNotice.value = null), 4000);
    // If we're viewing Sent or Drafts, refresh to show the change.
    const use = S.currentMailboxObj.value?.specialUse;
    if (use === "sent" || use === "drafts") void loadMessages();
  } catch (e) {
    if (!handleAuthError(e)) {
      S.composerError.value = e instanceof Error ? e.message : "Failed to send";
    }
    // Bring the composer back so the user can fix and retry.
    S.composerPendingSend.value = false;
  } finally {
    S.composerSending.value = false;
  }
}

/* ----------------------------- folder management ----------------------------- */

export function folderLabel(path: string): string {
  const box = S.mailboxes.value.find((m) => m.path === path);
  if (box?.specialUse) {
    const key = `folder.${box.specialUse}` as const;
    return t(key);
  }
  return box?.name ?? path.split(/[/.]/).pop() ?? path;
}

export async function createFolder(name: string, parentPath?: string): Promise<void> {
  S.folderActionError.value = null;
  try {
    await api.mailboxes.create({ name, parentPath });
    S.folderCreateParent.value = undefined; // close input
    await loadMailboxes();
    toast(t("toast.folderCreated", { name }));
  } catch (e) {
    if (!handleAuthError(e)) {
      S.folderActionError.value = e instanceof Error ? e.message : "Failed to create folder";
      // Auto-clear after 5 s
      setTimeout(() => { S.folderActionError.value = null; }, 5000);
    }
  }
}

export async function renameFolder(oldPath: string, newPath: string): Promise<void> {
  S.folderActionError.value = null;
  try {
    await api.mailboxes.rename({ oldPath, newPath });
    S.folderEditPath.value = null; // close inline input
    // Update currentMailbox if the renamed folder was active
    if (S.currentMailbox.value === oldPath) {
      S.currentMailbox.value = newPath;
    }
    await loadMailboxes();
  } catch (e) {
    if (!handleAuthError(e)) {
      S.folderActionError.value = e instanceof Error ? e.message : "Failed to rename folder";
      setTimeout(() => { S.folderActionError.value = null; }, 5000);
    }
  }
}

export async function deleteFolder(path: string): Promise<void> {
  S.folderActionError.value = null;
  try {
    await api.mailboxes.delete(path);
    // If the deleted folder was active, navigate to INBOX
    if (S.currentMailbox.value === path) {
      await selectMailbox("INBOX");
    }
    await loadMailboxes();
  } catch (e) {
    if (!handleAuthError(e)) {
      S.folderActionError.value = e instanceof Error ? e.message : "Failed to delete folder";
      setTimeout(() => { S.folderActionError.value = null; }, 5000);
    }
  }
}

export async function markFolderRead(path: string): Promise<void> {
  try {
    await api.markMailboxRead(path);
    if (path === S.currentMailbox.value) {
      S.messages.value = S.messages.value.map((m) => ({ ...m, seen: true }));
    }
    S.mailboxes.value = S.mailboxes.value.map((mb) => (mb.path === path ? { ...mb, unread: 0 } : mb));
    toast(t("toast.markedAllRead", { folder: folderLabel(path) }));
  } catch (e) {
    if (!handleAuthError(e)) toast(errorText(e, t("toast.actionFailed")), { tone: "error" });
  }
}

export async function emptyFolder(path: string): Promise<void> {
  const ok = await confirmDialog({
    title: t("confirm.empty.title", { folder: folderLabel(path) }),
    body: t("confirm.empty.body"),
    confirmLabel: t("common.emptyNow"),
    danger: true,
  });
  if (!ok) return;
  try {
    const res = await api.emptyMailbox(path);
    if (path === S.currentMailbox.value) {
      S.messages.value = [];
      S.messagesTotal.value = 0;
      closeReader();
    }
    await loadMailboxes();
    toast(t("toast.emptied", { n: res.removed }));
  } catch (e) {
    if (!handleAuthError(e)) toast(errorText(e, t("toast.actionFailed")), { tone: "error" });
  }
}

/* --------------------------------- misc --------------------------------- */

/** Print the open message (headers + body) through a throwaway iframe. */
export function printMessage(): void {
  const m = S.fullMessage.value;
  if (!m || typeof document === "undefined") return;
  const frame = document.createElement("iframe");
  frame.style.position = "fixed";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  frame.setAttribute("sandbox", "allow-same-origin allow-modals");
  const head = [
    `<h2 style="font-family:Georgia,serif;font-weight:500;margin:0 0 8px">${escapeHtml(m.subject || "(no subject)")}</h2>`,
    `<div style="font:12px system-ui;color:#555;margin-bottom:16px;border-bottom:1px solid #ddd;padding-bottom:8px">`,
    `${t("compose.from")}: ${escapeHtml(joinAddrs(m.from))}<br>${t("compose.to")}: ${escapeHtml(joinAddrs(m.to))}<br>`,
    m.cc.length ? `Cc: ${escapeHtml(joinAddrs(m.cc))}<br>` : "",
    `${t("compose.date")}: ${escapeHtml(new Date(m.date).toLocaleString())}</div>`,
  ].join("");
  const body = m.html ?? `<pre style="white-space:pre-wrap;font:13px system-ui">${escapeHtml(m.text ?? "")}</pre>`;
  frame.srcdoc = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(m.subject)}</title></head><body style="margin:24px;font:14px system-ui;color:#111">${head}${body}</body></html>`;
  frame.onload = () => {
    try {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
    } finally {
      setTimeout(() => frame.remove(), 1000);
    }
  };
  document.body.appendChild(frame);
}

export async function copyToClipboard(text: string, message?: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast(message ?? t("toast.copied"));
  } catch {
    toast(t("toast.copyFailed"), { tone: "error" });
  }
}
